import { useState } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/base/Container";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const CONTACT_EMAIL = "contact@yeqinbingdong.cn";

const scopeItems = [
  {
    icon: "ri-film-line",
    title: "影视作品资料",
    desc: "夜勤题材相关的电影与电视剧条目，包含导演、主演、类型、上映年份、片长等基础信息，以及剧情摘要与完整简介。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯与快讯",
    desc: "定档、幕后、重映等影视资讯，以及围绕具体作品的观察与报道。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "剧情解析与专题",
    desc: "针对剧情细节、符号隐喻与叙事结构的深度解析，以及围绕某一主题的合集式专题内容。",
  },
];

export default function About() {
  useDocumentMeta(
    "关于我们－夜勤病栋影视内容站",
    "了解夜勤病栋影视内容站的网站定位、内容范围、数据来源说明与联系方式。"
  );

  const [toast, setToast] = useState("");

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2000);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      showToast("邮箱地址已复制");
    } catch {
      showToast(`联系邮箱：${CONTACT_EMAIL}`);
    }
  };

  return (
    <div>
      <section className="bg-primary-800">
        <Container className="py-10 md:py-16">
          <nav className="flex items-center gap-1.5 text-xs text-primary-200 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-background-50 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">关于我们</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
            关于夜勤病栋
          </h1>
          <p className="mt-3 text-sm md:text-base text-primary-100 max-w-3xl leading-relaxed">
            夜勤病栋是一个聚焦夜勤题材影视作品的内容站。我们希望为影迷与影视爱好者提供一个资料完整、信息清晰、便于深入阅读的内容平台。
          </p>
        </Container>
      </section>

      <Container className="py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
              <i className="ri-compasses-2-line text-xl"></i>
            </span>
            <h2 className="font-heading font-bold text-foreground-950 mb-3">网站定位</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              面向喜爱夜勤题材影视作品的粉丝，以及对相关影视资讯感兴趣的普通观众。网站聚焦于「了解作品」这件事本身：提供作品的完整资料、剧情介绍与专题分析，帮助用户快速判断一部作品是否值得一看，也能满足深度影迷对细节与解读的需求。
            </p>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
              <i className="ri-stack-line text-xl"></i>
            </span>
            <h2 className="font-heading font-bold text-foreground-950 mb-3">内容范围</h2>
            <ul className="space-y-3">
              {scopeItems.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <i className={`${item.icon} text-primary-600 mt-0.5`}></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="w-11 h-11 rounded-lg bg-secondary-200 text-secondary-900 flex items-center justify-center mb-4">
              <i className="ri-database-2-line text-xl"></i>
            </span>
            <h2 className="font-heading font-bold text-foreground-950 mb-3">数据来源说明</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              站内影视条目、资讯与专题内容均由编辑团队整理与撰写，用于展示内容站的结构与阅读体验。当前页面展示的内容为演示数据，封面为示意图片，与真实作品不存在对应关系。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mt-3">
              本站仅提供文字与图片资讯，<strong className="text-foreground-800">不提供任何播放、下载或在线观看功能</strong>，也不存储任何影视视频资源。
            </p>
          </section>
        </div>

        <section className="mt-8 md:mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  联系我们
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                如果你对本站内容有补充、纠错或合作意向，欢迎通过邮件联系我们。点击邮箱地址可复制，或直接打开本地邮件客户端发送内容建议与反馈。
              </p>
            </div>

            <div className="lg:w-[420px] shrink-0 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="text-xs text-foreground-400 mb-2">联系邮箱</p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=%E3%80%90%E7%BD%91%E7%AB%99%E5%8F%8D%E9%A6%88%E3%80%91-%E5%A4%9C%E5%8B%A4%E7%97%85%E6%A0%8B`}
                  className="text-sm md:text-base font-medium text-primary-700 hover:text-accent-600 transition-colors break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  type="button"
                  onClick={copyEmail}
                  className="flex items-center gap-2 px-4 h-10 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-copy-line"></i>
                  复制邮箱
                </button>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex items-center gap-2 px-4 h-10 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  发送邮件
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 md:mt-10">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">常见问题</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                q: "网站提供在线播放或下载吗？",
                a: "不提供。本站仅提供文字与图片资讯，用于帮助用户了解作品信息与剧情，不涉及任何视频播放或下载。",
              },
              {
                q: "收藏功能需要注册登录吗？",
                a: "不需要。收藏记录保存在你自己的浏览器本地，刷新页面或重新访问都不会丢失，但更换设备或浏览器后不会同步。",
              },
              {
                q: "站内数据多久更新一次？",
                a: "影视条目与专题会随内容整理进度不定期更新，资讯栏目则会跟随行业动态持续补充。",
              },
              {
                q: "发现内容有误怎么办？",
                a: "欢迎通过上方邮箱告诉我们，附上页面名称与具体问题，我们会尽快核实并修正。",
              },
            ].map((item) => (
              <div key={item.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-medium text-foreground-900 mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-accent-600 mt-0.5"></i>
                  {item.q}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed pl-6">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </Container>

      {toast && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-md bg-foreground-950 text-background-50 text-sm max-w-[90vw] truncate">
          {toast}
        </div>
      )}
    </div>
  );
}